import * as React from "react";
import {useEffect, useState} from "react";
import {
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  useTheme
} from "@mui/material";
import User from "../../model/User";
import TextFieldValidator from "../../components/form/TextFieldValidator";
import {useResources} from "../../stores/ResourceProvider";
import ModelEditDialog from "../../components/model/ModelEditDialog";
import {
  CreateUserInput,
  ModelUserFilterInput,
  ModelUserInviteFilterInput,
  UpdateUserInput,
  UserRole,
  UserStatus,
  UserType
} from "../../API";
import IconicButton from "../../components/controls/IconicButton";
import EditIcon from "@mui/icons-material/Edit";
import DialogContentText from "@mui/material/DialogContentText";
import {Auth} from "aws-amplify";
import Visible from "../../components/Visible";
import DialogButton from "../../components/form/DialogButton";
import {getISODateToday, phoneToE164Format, phoneToNationalFormat} from "../../stores/StoreUtilities";
import Model from "../../model/Model";
import {ConvertKitTag, ISubscriber} from "../../apis/ConvertKitAPI";
import config from "react-global-configuration";
import {CopyToClipboard} from 'react-copy-to-clipboard';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import Review from "../../model/Review";

enum InviteOption {
  None = "None",
  Send = "Send",
  Resend = "Resend",
  Revoke = "Revoke"
}

const UserEditDialog = ({
  open,
  user,
  onClose,
  onSave,
  onDelete
}: {
  open?: boolean
  user: User
  onClose?(): void
  onSave?(update: User): void
  onDelete?(user: User): void
}) => {
  const [userModel, setUserModel] = useState<User>(user)
  const [userInviteUrl, setUserInviteUrl] = useState<string | undefined>()
  const [firstName, setFirstName] = useState<string>("")
  const [lastName, setLastName] = useState<string>("")
  const [email, setEmail] = useState<string>()
  const [editEmail, setEditEmail] = useState<boolean>(false)
  const [emailVerified, setEmailVerified] = useState<boolean>(false)
  const [phone, setPhone] = useState<string>()
  const [editPhone, setEditPhone] = useState<boolean>(false)
  const [phoneVerified, setPhoneVerified] = useState<boolean>(false)
  const [isSelf, setIsSelf] = useState<boolean>(false)
  const [userStatus, setUserStatus] = useState<UserStatus>(UserStatus.Created)
  const [userStatusOptions, setUserStatusOptions] = useState<UserStatus[]>([])
  const [inviteOptions, setInviteOptions] = useState<InviteOption[]>([])
  const [inviteOption, setInviteOption] = useState<InviteOption | undefined>()
  const [userType, setUserType] = useState<UserType>(UserType.Free)
  const [role, setRole] = useState<UserRole>(UserRole.Customer)
  const [customerId, setCustomerId] = useState<string | undefined>()
  const [advisorAccess, setAdvisorAccess] = useState<boolean>(false)
  const [newEmail, setNewEmail] = useState<string>("")
  const [newPhone, setNewPhone] = useState<string>("")
  const [emailCode, setEmailCode] = useState<string>("")
  const [phoneCode, setPhoneCode] = useState<string>("")
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [message, setMessage] = useState<string>("")
  const [isConfirmingEmail, setIsConfirmingEmail] = useState<boolean>(false)
  const [isConfirmingPhone, setIsConfirmingPhone] = useState<boolean>(false)
  const [isProcessing, setIsProcessing] = useState<boolean>(false)

  const theme = useTheme()
  const { userStore, accountStore, modelStore, confirm, authAPI, billingAPI, convertKitAPI,notify } = useResources()

  const styles = {
    actions: {
      display:"flex",
      flexDirection:"row",
      width:"100%",
      justifyContent:"space-between",
      alignItems:"center"
    },
    row: {
      maxHeight: 48
    },
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      color: theme.palette.grey["700"]
    },
    instructions: {
      marginTop: theme.spacing(2),
      marginBottom: 0
    },
    check: {
      height: 16,
      color: theme.palette.secondary.dark
    }
  }

  useEffect(() => {
    setFirstName(user.firstName)
    setLastName(user.lastName)
    setEmail(user.email)
    setPhone(phoneToNationalFormat(user.phone))
    setEmailVerified(user.emailVerified)
    setPhoneVerified(user.phoneVerified)
    setUserStatus(user.userStatus)
    setUserType(user.userType)
    setRole(user.role)
    setCustomerId(user.customerId)
    setAdvisorAccess(user.advisorAccess)
    setIsSelf(userStore?.user?.id === user.id)
    setIsOpen(open === true)
    setUserStatusOptions(getUserStatusOptions(user))
    setInviteOptions(getInviteOptions(user))
    setInviteOption(InviteOption.None)
    if (user.userStatus === UserStatus.Invited) {
      loadUserInvite(user)
    }
  }, [user, open])

  const loadUserInvite = async (user: User) => {
    const filter: ModelUserInviteFilterInput = {
      userId: {eq: user.id}
    }

    const invites = await accountStore.listUserInvitesByAccount(user.accountId, filter)
    if (invites.length === 1) {
      setUserInviteUrl(`${config.get("baseUrl")}/invite/${invites[0].id}`)
    }
  }

  const getUserStatusOptions = (user: User): UserStatus[] => {
    let options: UserStatus[] = []

    if (userStore.isAdvisorOrAdmin) {
      if (user.userStatus === UserStatus.Created || user.userStatus === UserStatus.Invited) {
        options = [UserStatus.Created, UserStatus.Invited, UserStatus.Active]
      } else if (user.userStatus === UserStatus.Active || user.userStatus === UserStatus.Cancelled || user.userStatus === UserStatus.Deleted) {
        options = [UserStatus.Created, UserStatus.Active, UserStatus.Cancelled]
      }
    }

    return options
  }

  const getInviteOptions = (user: User): InviteOption[] => {
    let options: InviteOption[] = []

    if (userStore.isAdvisorOrAdmin) {
      if (user.userStatus === UserStatus.Created) {
        options = [InviteOption.None, InviteOption.Send]
      } else if (user.userStatus === UserStatus.Invited) {
        options = [InviteOption.None, InviteOption.Resend, InviteOption.Revoke]
      } else {
        options = [InviteOption.None]
      }
    }

    return options
  }

  const handleClose = async (event: any) => {
    if (onClose) {
      onClose()
    }
  }

  const handleSave = async (event: any) => {
    setMessage("")

    try {
      setIsProcessing(true)
      if (!userModel.id) {
        if (await isEmailInUse(email!)) {
          setMessage("This email is already in use.")
          return
        }
        const input: CreateUserInput = {
          userStatus: userStatus,
          userType: userType,
          accountId: userModel.accountId,
          firstName: firstName,
          lastName: lastName,
          email: email!,
          phone: phoneToE164Format(phone ?? ""),
          role: role,
          advisorAccess: advisorAccess,
          startDate: getISODateToday(),
          customerId: customerId ?? null,
          clientOn: userType === UserType.Client ? getISODateToday() : null
        }
        const user = await userStore.createUser(input, false)

        if (user) {
          await modelStore.createBaseModel(user)

          if (inviteOption === InviteOption.Send) {
            await accountStore.sendUserInvite(user)
          }

          if (onSave) {
            onSave(user)
          }
        }
      } else {
        let updateUser = false

        if (editEmail) {
          if (!isConfirmingEmail) {
            const alias = await authAPI?.getAlias(newEmail)

            if (alias && alias.status === "VERIFIED") {
              setMessage("The new email is already in use.")
              return
            }

            const authUser = await Auth.currentAuthenticatedUser()
            if (authUser) {
              const result = await Auth.updateUserAttributes(authUser, { email: newEmail.toLowerCase() })
              if (result) {
                setIsConfirmingEmail(true)
                return
              }
            }
          } else {
            const result = await Auth.verifyCurrentUserAttributeSubmit('email', emailCode)
              if (result) {
                notify!.show("success", "Email address updated!")
                setIsConfirmingEmail(false)
                setEmail(newEmail)
                setEditEmail(false)
                updateUser = true
              }
          }
        }

        if (editPhone) {
          if (!isConfirmingPhone) {
            const intlPhone = phoneToE164Format(newPhone)
            if (intlPhone) {
              const alias = await authAPI?.getAlias(intlPhone)

              if (alias && alias.status === "VERIFIED") {
                setMessage("The new phone number is already in use.")
                return
              }
            }

            const authUser = await Auth.currentAuthenticatedUser()
            if (authUser) {
              const result = await Auth.updateUserAttributes(authUser, {phone_number: intlPhone})
              if (result) {
                setIsConfirmingPhone(true)
                return
              }
            }
          } else {
            const result = await Auth.verifyCurrentUserAttributeSubmit('phone_number', phoneCode)
            if (result) {
              notify!.show("success", "Phone number updated!")
              setIsConfirmingPhone(false)
              setPhone(newPhone)
              setEditPhone(false)
              updateUser = true
            }
          }
        } else {
          updateUser = true
        }

        if (updateUser) {
          if (email !== userModel.email && await isEmailInUse(email!)) {
            setMessage("This email is already in use.")
            return
          }
          const update: UpdateUserInput = {
            id: user.id,
            accountId: user.accountId,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email,
            advisorAccess
          }
          if (editEmail) {
            update.email = newEmail
            update.emailVerified = true
          }
          if (editPhone) {
            update.phone = phoneToE164Format(newPhone)
            update.phoneVerified = true
          }

          if (isAdvisorOrAdmin && userStatus !== user.userStatus) {
            update.userStatus = userStatus
          }
          if (isAdvisorOrAdmin && userType !== user.userType) {
            update.userType = userType
            if (userType === UserType.Client) {
              update.clientOn = getISODateToday()
            } else if (user.userType === UserType.Premium && user.customerId) {
              const result = await cancelSubscription(user)
            }
          }
          if (isAdvisorOrAdmin && role !== user.role) {
            update.role = role
          }
          if (isAdvisorOrAdmin && customerId !== user.customerId) {
            update.customerId = customerId
          }

          const updatedUser = await userStore.updateUser(update)

          if (updatedUser) {
            if (inviteOption === InviteOption.Send || inviteOption === InviteOption.Resend) {
              await accountStore.sendUserInvite(user)
            } else if (inviteOption === InviteOption.Revoke) {
              await accountStore.revokeUserInvite(user)
            }
          }

          if (updatedUser) {
            await updateConvertKitSubscriber(user, update)
          }

          if (onSave && updatedUser) {
            onSave(updatedUser)
          } else if (onClose) {
            onClose()
          }
        }

      }
    } catch (err: any) {
      setIsProcessing(false)
      notify.show('error', err.message)
    }
  }

  const cancelSubscription = async (user: User) => {
    if (user.userType === UserType.Premium) {
      // TODO
    }
    return "SUCCESS"
  }

  const updateConvertKitSubscriber = async (user: User, input: UpdateUserInput) => {
    // Get subscriber
    const response = await convertKitAPI.getSubscriber(user.email)
      .catch((err: any) => {
        // Ignore
        console.log(`Subscriber not found`)
      })

    if (response && response.subscribers && response.subscribers.length > 0) {
      console.log(`Got Subscriber`)
      // Update subscriber
      const subscriber = response.subscribers[0]
      const update: ISubscriber = {}
      if (input.email) {
        update.email = input.email
      }
      if (input.firstName) {
        update.first_name = input.firstName
      }
      if (input.lastName) {
        update.last_name = input.lastName
      }
      const result = await convertKitAPI.putSubscriber(subscriber.id, update)
        .catch ((err: any) => {
          // Ignore
          console.log(`Unable to update subcriber`)
        })
    }
  }

  const isEmailInUse = async (email: string) => {
    // Confirm the email isn't already in use by another user
    const filter: ModelUserFilterInput = {
      email: {eq: email.toLowerCase()},
      userStatus: {ne: UserStatus.Deleted}
    }
    const users = await accountStore.listUsersByAccount(userModel.accountId, filter)
    return (users.length > 0 && users[0].id !== userModel.id)
  }

  const handleDelete = async () => {
    if (onDelete) {
      if (user && user.userStatus === UserStatus.Deleted) {
        confirm.show('Confirm Delete', `Are you sure you want to fully delete this user record?`,
          ['Delete', 'Cancel'],
          async () => {
            const deleted = await accountStore.deleteUser(user.id)
            if (deleted && onDelete) {
              deleted.referrer = "Fully Deleted" // Flag to ManageUsersView
              notify.show("success", `User (${deleted.fullName}) has been fully deleted.`)
              onDelete(deleted)
            }
            return true
          })
      } else {
        confirm.show('Confirm Delete', `Are you sure you want to delete this user? All user data will be permanently deleted and any subscription cancelled.`,
          ['Delete', 'Cancel'],
          async () => {
            try {
              const user = await userStore.getUser(userModel.id)
              if (user) {
                // Delete Reviews
                if (user.reviews.length > 0) {
                  const reviewPromises: Promise<Review | undefined>[] = []
                  user.reviews.forEach((r: any) => {
                    reviewPromises.push(userStore.deleteReview(r.id))
                  })
                  await Promise.all(reviewPromises)
                }

                // Delete models
                const promises: Promise<Model | undefined>[] = []
                user.models.forEach((model: Model) => {
                  promises.push(modelStore.deleteModel(model.id, true))
                })
                await Promise.all(promises)

                await convertKitAPI.addTag(ConvertKitTag.Cancellation, user.email)

                // Cancel Stripe subscription
                if (user.userType === UserType.Premium && user.customerId) {
                  const customer = await billingAPI.getCustomer(user.customerId)
                  if (customer && customer.subscriptions && customer.subscriptions.data && customer.subscriptions.data.length > 0) {
                    const subscriptionId = customer.subscriptions.data[0].id
                    const result = await billingAPI.cancelSubscription(subscriptionId)
                    console.log(`cancelSubscription result`, result)
                  }
                }

                // Mark user deleted
                const update: UpdateUserInput = {
                  id: user.id,
                  userStatus: UserStatus.Deleted,
                  deletedOn: getISODateToday(),
                  deletedReason: `Deleted by ${userStore.user!.role}: ${userStore.user!.fullName}`
                }
                const deleted = await userStore.updateUser(update)
                if (deleted) {
                  // Delete cognito user
                  const result = await authAPI.deleteUser(user.id)
                  console.log(`deleteUser result:`, result)
                }

                if (deleted && onDelete) {
                  notify.show("success", `User (${deleted.fullName}) has been deleted.`)
                  onDelete(deleted)
                }
              }
            } catch (err: any) {
              notify.show("error", err.message)
            }
            return true
          })
      }
    }
  }

  const handleResendCode = () => {
    setMessage("")

    if (isConfirmingEmail) {
      Auth.currentAuthenticatedUser()
        .then(user => {
          setIsProcessing(true)
          Auth.verifyCurrentUserAttribute("email")
            .then(data => {
              setIsConfirmingEmail (true)
              setIsProcessing(false)
              notify!.show("success", "Confirmation email resent!")
            })
            .catch(err => {
              setMessage(err.message)
              setIsProcessing(false)
            });
        })
        .catch(err => {
          setMessage(err.message)
        });
    } else if (isConfirmingPhone) {
      Auth.currentAuthenticatedUser()
        .then(user => {
          setIsProcessing(true)
          Auth.verifyCurrentUserAttribute("phone_number")
            .then(data => {
              setIsConfirmingPhone (true)
              setIsProcessing(false)
              notify!.show("success", "Confirmation text resent!")
            })
            .catch(err => {
              setMessage(err.message)
              setIsProcessing(false)
            });
        })
        .catch(err => {
          setMessage(err.message)
        });
    }
  }

  const onCopyLink = () => {
    notify!.show('success', 'Copied Invite link to clipboard.')
  }

  if (!userModel || !isOpen) {
    return null
  }

  const isAdvisorOrAdmin = userStore.isAdvisorOrAdmin
  const isAdmin = userStore.isAdmin
  const isAdvisor = userStore.isAdvisor

  const emailDisabled = userModel.userStatus !== UserStatus.Created && userModel.userStatus !== UserStatus.Invited
  const phoneDisabled = userModel.userStatus !== UserStatus.Created && userModel.userStatus !== UserStatus.Invited
  const userStatusEnabled = userStore.isAdvisorOrAdmin /* && userModel.userStatus &&
    (userModel.userStatus === UserStatus.Active || userModel.userStatus === UserStatus.Cancelled || userModel.userStatus === UserStatus.Deleted) */
  const inviteEnabled = userStore.isAdvisorOrAdmin &&
    (!userModel.userStatus || userModel.userStatus === UserStatus.Created || userModel.userStatus === UserStatus.Invited)

  return (
    <React.Fragment>
      <ModelEditDialog title="User Settings" open={isOpen}
                       onCancel={handleClose}
                       onSave={handleSave}
                       confirmDelete={false}
                       onDelete={onDelete && userModel.id ? handleDelete : undefined}
        >
          {message &&
            <DialogContentText color="error">
              {message}
            </DialogContentText>
          }
          <TextFieldValidator
            autoFocus={true}
            type="text"
            variant="standard"
            validators={{ required: true }}
            name="firstName"
            label="First Name"
            value={firstName}
            onChange={(event: any) => setFirstName(event.target.value)}
            fullWidth
          />
          <TextFieldValidator
            type="text"
            variant="standard"
            validators={{ required: true }}
            name="lastName"
            label="Last Name"
            value={lastName}
            onChange={(event: any) => setLastName(event.target.value)}
            fullWidth
          />
          <TextFieldValidator
            type="text"
            variant="standard"
            validators={{ required: true, isEmail: null }}
            name="email"
            label="Email"
            value={email}
            onChange={(event: any) => setEmail(String(event.target.value).toLowerCase())}
            fullWidth
            disabled={emailDisabled}
            InputProps={isSelf ? {
              endAdornment: (
                <InputAdornment position="end">
                  <IconicButton onClick={() => setEditEmail(true)}>
                    <EditIcon />
                  </IconicButton>
                </InputAdornment>
              ),
            } : emailVerified ? {
              endAdornment: (
                <InputAdornment position="end">
                  <CheckIcon sx={styles.check}/>
                </InputAdornment>
              )} : {}
            }
          />
          {editEmail &&
            <TextFieldValidator
              margin="dense"
              name="newEmail"
              label="New Email"
              type="email"
              variant="standard"
              validators={{ required: true, isEmail: true }}
              value={newEmail}
              onChange={(event: any) => setNewEmail(String(event.target.value).toLowerCase())}
              disabled={isConfirmingEmail}
              fullWidth
            />
          }
          <TextFieldValidator
            type="text"
            variant="standard"
            validators={{ required: false, isMobilePhone: true }}
            name="phone"
            label="Phone"
            value={phone}
            onChange={(event: any) => setPhone(String(event.target.value).toLowerCase())}
            fullWidth
            disabled={phoneDisabled}
            InputProps={isSelf ? {
              endAdornment: (
                <InputAdornment position="end">
                  <IconicButton onClick={() => setEditPhone(true)}>
                    <EditIcon />
                  </IconicButton>
                </InputAdornment>
              ),
            } : phoneVerified ? {
              endAdornment: (
                <InputAdornment position="end">
                  <CheckIcon sx={styles.check}/>
                </InputAdornment>
              )} : {}
            }
          />
          {editPhone &&
            <TextFieldValidator
              margin="dense"
              name="newPhone"
              label="New Phone"
              type="text"
              variant="standard"
              validators={{ required: true, isMobilePhone: true }}
              value={newPhone}
              onChange={(event: any) => setNewPhone(String(event.target.value).toLowerCase())}
              disabled={isConfirmingPhone}
              fullWidth
            />
          }
          {isAdvisorOrAdmin &&
            <TextFieldValidator
              type="text"
              validators={{ required: true }}
              required
              name="role"
              label="User Role"
              variant="standard"
              autocompleteOptions={{
                freeSolo: false,
                options: Object.values(UserRole),
                value: role,
                onChange: (event: any, value: UserRole, reason: any) => {
                  setRole(value)
                }
              }}
            />
          }
          {isAdvisorOrAdmin &&
            <TextFieldValidator
              type="text"
              validators={{ required: true }}
              required
              name="userType"
              label="User Type"
              variant="standard"
              autocompleteOptions={{
                freeSolo: false,
                options: Object.values(UserType),
                value: userType,
                onChange: (event: any, value: UserType, reason: any) => {
                  setUserType(value)
                }
              }}
            />
          }
          {isAdvisorOrAdmin && userType === UserType.Premium &&
            <TextFieldValidator
              type="text"
              validators={{ required: false }}
              name="customerId"
              label="Stripe Customer ID"
              variant="standard"
              value={customerId}
              onChange={(event: any) => setCustomerId(event.target.value)}
              fullWidth
            />
          }
          {userStatusEnabled &&
            <TextFieldValidator
              type="text"
              validators={{ required: true }}
              required
              name="userStatus"
              label="User Status"
              variant="standard"
              autocompleteOptions={{
                freeSolo: false,
                options: userStatusOptions,
                value: userStatus,
                onChange: (event: any, value: UserStatus, reason: any) => {
                  setUserStatus(value)
                }
              }}
            />
          }
          {inviteEnabled &&
            <FormGroup>
              <FormLabel sx={styles.formLabel}>Invitation Email</FormLabel>
              <RadioGroup aria-label="inviteOption" name="inviteOption" value={String(inviteOption)} row
                          onChange={(event: any) => {
                            setInviteOption(event.target.value)
                            if (event.target.value === "Send" || event.target.value === "Resend") {
                              setUserStatus(UserStatus.Invited)
                            } else if (event.target.value === "Revoke") {
                              setUserStatus(UserStatus.Created)
                            }
                          }}>
                {inviteOptions.map((option: InviteOption) =>
                    <FormControlLabel
                      value={option}
                      control={<Radio color="secondary"/>}
                      label={option}
                      labelPlacement="end"
                    />
                  )}
              </RadioGroup>
            </FormGroup>
          }
          {userStatus === UserStatus.Invited && userInviteUrl &&
            <TextFieldValidator
              margin="dense"
              name="userInviteUrl"
              label="Invitation Link"
              type="text"
              validators={{required:false}}
              value={userInviteUrl}
              disabled={true}
              fullWidth
              variant="standard"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CopyToClipboard
                      text={userInviteUrl ?? ""}
                      onCopy={onCopyLink}>
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => {}}
                      >
                        <ContentCopyIcon/>
                      </IconButton>
                    </CopyToClipboard>
                  </InputAdornment>
                )
              }}

            />
          }
          {isAdvisorOrAdmin &&
            <FormGroup>
              <FormLabel sx={styles.formLabel}>Advisor Access</FormLabel>
              <RadioGroup aria-label="advisorAccess" name="advisorAccess" value={String(advisorAccess)} row
                          onChange={(event: any) => {
                            setAdvisorAccess(event.target.value === "true")
                          }}>
                <FormControlLabel
                  value="false"
                  control={<Radio color="secondary"/>}
                  label="No"
                  disabled={!(isAdmin || (isAdvisor && !userModel.id) || (isAdvisor && userModel.id && userModel.advisorAccess) || (isAdvisor && userModel.userStatus === UserStatus.Created))}
                  labelPlacement="end"
                />
                <FormControlLabel
                  value="true"
                  control={<Radio color="secondary"/>}
                  label="Yes"
                  disabled={!(isAdmin || (isAdvisor && !userModel.id) || (isAdvisor && userModel.id && userModel.advisorAccess) || (isAdvisor && userModel.userStatus === UserStatus.Created))}
                  labelPlacement="end"
                />
              </RadioGroup>
            </FormGroup>
          }
          <Visible cond={isConfirmingEmail}>
            <DialogContentText sx={styles.instructions}>
              Please check your new email for a confirmation code.
            </DialogContentText>
            <TextFieldValidator
              margin="dense"
              name="emailCode"
              label="Confirmation Code"
              type="text"
              variant="standard"
              value={emailCode}
              validators={{ required: true, matches: "^\\d{6}$" }}
              onChange={(event: any) => setEmailCode(event.target.value)}
              fullWidth
            />
            <DialogButton variant="tertiary" onClick={handleResendCode}>
              Resend confirmation code
            </DialogButton>
          </Visible>
        <Visible cond={isConfirmingPhone}>
          <DialogContentText sx={styles.instructions}>
            Please check your new phone number for a confirmation code.
          </DialogContentText>
          <TextFieldValidator
            margin="dense"
            name="phoneCode"
            label="Confirmation Code"
            type="text"
            variant="standard"
            value={phoneCode}
            validators={{ required: true, matches: "^\\d{6}$" }}
            onChange={(event: any) => setPhoneCode(event.target.value)}
            fullWidth
          />
          <DialogButton variant="tertiary" onClick={handleResendCode}>
            Resend confirmation code
          </DialogButton>
        </Visible>
      </ModelEditDialog>
    </React.Fragment>
  )
}

export default UserEditDialog